import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Theme,
  useTheme,
  useMediaQuery,
  Dialog,
  Tooltip,
} from '@mui/material';
import { AppRow } from '../../layout/appRow';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { ViewMode } from '../../models/preferences';
import { EmployeesSummary, MonthSelect } from '../../models/reports';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

import { Activity } from '../../models/activity';
import { SuccessfulDialog } from '../../models/dialog';

import {
  formatActivityDate,
  formatDisplayReportsDate,
  getDateByWeekNumber,
  getEndDateAccording,
  getStartDateAccording,
} from '../../services/dateService';
import { format, formatISO } from 'date-fns';
import { ActivityForm, ActivityFormData } from '../activity/activityForm';
import useAppSnackbar from '../../hooks/useAppSnackbar';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import {
  useGetReportsActivitiesQuery,
  useAddReportsActivityMutation,
  useEditReportsActivityMutation,
  useDeleteReportsActivityMutation,
  reportsActivitiesService,
  reportsActivityQueryTag,
} from '../../store/reports/reportsActivitiesService';

import { AppActivityViews } from '../app/appActivityViews';
import { AppConfirmationDelete } from '../app/confirmation/appConfiramationDelete';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import { ROLES_ID } from '../../models/employee';
import { AppViewModeSelector } from '../app/appViewModeSelector';
import { useDisplayDate } from '../../hooks/useDisplayDate';
import { useHandleDisplayDate } from '../../hooks/useHandleDisplayDate';
import { getNextDate, getPreviousDate } from '../../services/reportsService';
import { ReportsEmployeeWorktime } from './reportsWorktimeDisplay';
import { AppActivityTableViews } from '../app/appActivityTableViews';
import { EmployeeOption } from './reportsTeamLeadSelect';
import { useReportsFilters } from '../../context/reports/useReportsCtx';
import { useSelector } from 'react-redux';
import { selectEmployeeCardState } from '../../store/reports/reportsSlice';
import { generateActivityFormData } from '../../services/activityService';
import { convertHoursToMinutes } from '../../services/conversionService';
import { generateUserLabel, generateUserWithIdLabel } from '../../services/employeeService';
import { useAppDispatch } from '../../hooks';
import {
   formatDateOnly,
} from '../../services/dateService';

export interface ReportsEmployeeItemProps {
  data: EmployeesSummary;
  startDateFilter: string;
  endDateFilter: string;
  showTeamLeadIcon: string | false | null | undefined | boolean;
  projectIdFilter: string | null;
  handleAddTeamLeadTab?: (employee: EmployeeOption) => void;
  reloadEmployeeData: (
    employeeId: string,
    employeeName: string,
    lastLoggedActivity: ActivityFormData | null
  ) => void;
}

const makeStyles = (theme: Theme) => ({
  accordion: {
    width: '100%',
    p: 0.5,
    mb: 2,
    '&:before': {
      display: 'none',
    },
    borderRadius: '5px',
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      mt: 0,
      mb: 0,
    },
  },
  accordionSummaryInnerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: '40px',
  },
  name: {
    color: ColorPalette.RAISIN_BLACK,
    display: 'inline-flex',
    mr: 2,
  },
  toolboxWrapper: {
    ml: -1,
    mt: -1,
    mb: 1,
  },
  toolbox: {
    display: 'inline-flex',
  },
  iconBtn: {
    color: ColorPalette.DARK_GRAY,
  },
  peopleMarker: {
    color: ColorPalette.DARK_GRAY,
    marginLeft: '14px',
  },
  viewModebtn: {
    fontSize: '14px',
    mr: 0.5,
  },
  workedTime: {
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    color: ColorPalette.DARK_GRAY,
    mr: 2,
  },
  workedTimeWarning: {
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    color: ColorPalette.WARNING_RED,
    mr: 2,
  },
  workedTimeFulfilled: {
    display: 'inline-flex',
    alignItems: 'center',
    mr: 2,
  },
  viewModeText: {
    display: 'flex',
    alignItems: 'center',
    mr: 1,
  },
  currentPeriodRow: {
    justifyContent: 'space-between',
    mr: 2,
    mb: 1,

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
    },
  },
  buttonArrow: {
    color: ColorPalette.DARK_GRAY,
  },
  buttonDate: {
    pt: 1.5,
  },
  gradientBtnTypography: { display: 'flex', alignItems: 'center' },
  gradientBtn: {
    fontFamily: TypographyFamily.NEXA,
    textDecorationColor: ColorPalette.GREEN,
    textUnderlineOffset: 2,
    marginLeft: '16px',
  },
  plusSign: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '25px',
  },
  currentPeriodBox: { display: 'flex', justifyContent: 'flex-start' },
  customPeriodBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: 2,
    marginTop: 3,
  },
});

export function ReportsEmployeeItem({
  data,
  reloadEmployeeData,
  startDateFilter,
  endDateFilter,
  projectIdFilter,
  showTeamLeadIcon,
  handleAddTeamLeadTab,
}: ReportsEmployeeItemProps): JSX.Element {
  const theme = useTheme();
  const styles = makeStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { onErrorSnackbar } = useAppSnackbar();

  const { employee, loggedHours, workingHours } = data;
  const [dateActivity, setDateActivity] = useState<Date>(
    new Date(formatDateOnly(startDateFilter))
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { employeeId, lastLoggedActivity } = useSelector(
    selectEmployeeCardState
  );
  const [isOpenViewMode, setIsOpenViewMode] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState(ViewMode.OLD_VIEW);
  const [isOpenConfirmation, setIsOpenConfirmation] = useState(false);
  const [isOpenAddActivity, setIsOpenAddActivity] = useState<boolean>(false);
  const [isOpenEditActivity, setIsOpenEditActivity] = useState(false);
  const [successfulDialog, setSuccessfulDialog] = useState<SuccessfulDialog>({
    isOpen: false,
    message: null,
  });
  const [selectedActivity, setSelectedActivity] = useState<ActivityFormData>(
    generateActivityFormData()
  );

  const filters = useReportsFilters();
  const dispatch = useAppDispatch();

  const isCustomDate = useMemo(() => {
    return filters && filters.monthSelect === MonthSelect.CUSTOM;
  }, [filters]);

  const isTeamLead = useMemo(() => {
    return data.employee.permissionGroups.some(
      (group) => group.id === ROLES_ID.TEAM_LEAD
    );
  }, [data]);

  const messageConfirmationDelete = useMemo(
    () =>
      `Delete activity ${selectedActivity?.project?.label} - ${selectedActivity?.task?.name} (${selectedActivity?.hours}h) ?`,
    [selectedActivity]
  );

  const startDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(startDateFilter)), 'yyyy-MM-dd')
        : format(getStartDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, startDateFilter]
  );
  const endDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(endDateFilter)), 'yyyy-MM-dd')
        : format(getEndDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, endDateFilter]
  );

  const startDateFormatted = useMemo(
    () => formatDisplayReportsDate(startDate),
    [startDate]
  );
  const endDateFormatted = useMemo(
    () => formatDisplayReportsDate(endDate),
    [endDate]
  );

  const { displayDate } = useDisplayDate({ viewMode, dateActivity, isMobile });

  const {
    isLoading,
    isFetching,
    data: activities = [],
  } = useGetReportsActivitiesQuery(
    {
      startDate,
      endDate,
      employeeId: data.employee.id,
      roleId: ROLES_ID.TEAM_LEAD,
      projectId: projectIdFilter,
    },
    { skip: !isExpanded }
  );

  const [addActivity, { isLoading: isLoadingAddActivity }] =
    useAddReportsActivityMutation();
  const [editActivity, { isLoading: isLoadingEditActivity }] =
    useEditReportsActivityMutation();
  const [deleteActivity, { isLoading: isLoadingDeleteActivity }] =
    useDeleteReportsActivityMutation();

  const isLoadingAll = useMemo(
    () =>
      isLoading ||
      isFetching ||
      isLoadingAddActivity ||
      isLoadingEditActivity ||
      isLoadingDeleteActivity,
    [
      isFetching,
      isLoading,
      isLoadingAddActivity,
      isLoadingDeleteActivity,
      isLoadingEditActivity,
    ]
  );

  const isAddActivityButtonVisible = useMemo(
    () => viewMode === ViewMode.DAY,
    [viewMode]
  );

  useEffect(() => {
    if (employeeId && data.employee.id === employeeId) {
      setIsExpanded(true);
      setSelectedActivity({
        id: lastLoggedActivity?.id ? lastLoggedActivity.id : '',
        project: lastLoggedActivity?.project
          ? lastLoggedActivity.project
          : null,
        task: lastLoggedActivity?.task ? lastLoggedActivity.task : null,
        date: formatISO(dateActivity),
        hours: lastLoggedActivity?.hours ? lastLoggedActivity.hours : '',
        comment: lastLoggedActivity?.comment ? lastLoggedActivity.comment : '',
        ticketIdentification: lastLoggedActivity?.ticketIdentification ? lastLoggedActivity.ticketIdentification: '',
        isAMS: lastLoggedActivity?.isAMS || false,
      });
    } else {
      setSelectedActivity((prevSelectedActivity) => ({
        ...prevSelectedActivity,
        date: formatISO(dateActivity),
      }));
    }
  }, [data.employee.id, dateActivity, employeeId, lastLoggedActivity]);

  const onDayActivities = useCallback((activityDate: string) => {
    setDateActivity(new Date(formatDateOnly(activityDate)));
    setSelectedActivity((prevState) => ({
      ...prevState,
      date: activityDate,
    }));
    setViewMode(ViewMode.DAY);
  }, []);

  const handleOpenAddActivity = useCallback(() => {
    setIsOpenAddActivity(true);
  }, []);

  const handleCloseAddActivity = useCallback(() => {
    setIsOpenAddActivity(false);
  }, []);

  const handleOpenEditActivity = useCallback(() => {
    setIsOpenEditActivity((prev) => !prev);
  }, []);

  const onCloseDeleteDialog = useCallback(() => {
    setIsOpenConfirmation(false);
    setSelectedActivity(generateActivityFormData());
  }, []);

  const onCloseConfirmDialog = useCallback(() => {
    setSuccessfulDialog({
      isOpen: false,
      message: null,
    });
    setSelectedActivity(generateActivityFormData());
  }, []);

  const onDeleteActivity = useCallback((activity: Activity) => {
    setSelectedActivity(generateActivityFormData(activity));
    setIsOpenConfirmation(true);
  }, []);

  const onEditActivity = useCallback((activity: Activity) => {
    setSelectedActivity(generateActivityFormData(activity));
    setIsOpenEditActivity(true);
  }, []);

  const onAddActivity = useCallback(
    (addDate: string) => {
      setDateActivity(new Date(formatDateOnly(addDate)));
      setSelectedActivity((prevState) => ({
        ...prevState,
        date: addDate,
      }));
      handleOpenAddActivity();
    },
    [handleOpenAddActivity]
  );

  const onWeekActivities = useCallback(
    (weekNumber: number) => {
      setDateActivity(getDateByWeekNumber(weekNumber, dateActivity));
      setViewMode(ViewMode.WORK_WEEK);
    },
    [dateActivity]
  );

  const onSubmitAddActivity = useCallback(
    (formData: ActivityFormData<Date>) => {
      const payload = {
        employeeId: employee.id,
        comment: formData.comment,
        date: formatActivityDate(formData.date),
        minutes: convertHoursToMinutes(Number(formData.hours)),
        taskId: formData.task?.id || '',
        ticketIdentification: formData.ticketIdentification || '',
      };

      addActivity(payload)
        .then(() => {
          handleOpenAddActivity();
          reloadEmployeeData(employee.id, employee.name, {
            id: formData.id,
            project: formData.project,
            task: formData.task,
            hours: formData.hours,
            comment: formData.comment,
            date: formatISO(formData.date),
            ticketIdentification: formData.ticketIdentification,
            isAMS: formData.isAMS,
          });
        })
        .catch(onErrorSnackbar);
    },
    [
      addActivity,
      employee.id,
      employee.name,
      handleOpenAddActivity,
      reloadEmployeeData,
      onErrorSnackbar,
    ]
  );

  const onSubmitEditActivity = useCallback(
    (formData: ActivityFormData<Date>) => {
      if (selectedActivity) {
        const payload = {
          comment: formData.comment,
          date: formatActivityDate(formData.date),
          minutes: convertHoursToMinutes(Number(formData.hours)),
          taskId: formData.task?.id || '',
          employeeId: employee.id,
          ticketIdentification: formData.ticketIdentification,
        };

        editActivity({
          idActivity: selectedActivity.id,
          editActivityInfo: payload,
        })
          .then(() => {
            setIsOpenEditActivity(false);
            setSelectedActivity(generateActivityFormData());
            reloadEmployeeData(employee.id, employee.name, lastLoggedActivity);
          })
          .catch(onErrorSnackbar);
      }
    },
    [
      selectedActivity,
      employee.id,
      employee.name,
      editActivity,
      onErrorSnackbar,
      reloadEmployeeData,
      lastLoggedActivity,
    ]
  );

  const onConfirmDeleteActivity = useCallback(() => {
    if (!selectedActivity) {
      return;
    }
    deleteActivity(selectedActivity.id)
      .then(() => {
        setIsOpenConfirmation(false);
        setSuccessfulDialog({
          isOpen: true,
          message: 'Successfully deleted',
        });

        let lastActivity = lastLoggedActivity;

        if (
          lastLoggedActivity &&
          lastLoggedActivity.project?.id === selectedActivity.project?.id &&
          lastLoggedActivity.task?.id === selectedActivity.task?.id &&
          lastLoggedActivity.hours === selectedActivity.hours &&
          lastLoggedActivity.comment === selectedActivity.comment
        ) {
          lastActivity = null;
        }

        reloadEmployeeData(employee.id, employee.name, lastActivity);
      })
      .then(() => {
        setTimeout(() => {
          setSuccessfulDialog({
            isOpen: false,
            message: null,
          });

          setSelectedActivity(generateActivityFormData());
        }, 2000);
      })
      .catch(onErrorSnackbar);
  }, [
    selectedActivity,
    deleteActivity,
    onErrorSnackbar,
    lastLoggedActivity,
    reloadEmployeeData,
    employee.id,
    employee.name,
  ]);

  const handleOpenViewMode = useCallback(() => {
    setIsOpenViewMode((prev) => !prev);
  }, []);

  const handleChangeViewMode = useCallback(
    (viewModeArg: ViewMode) => setViewMode(viewModeArg),
    []
  );

  const { handleDisplayDate } = useHandleDisplayDate({
    viewMode,
    handleChangeViewMode,
  });

  const handlePreviousDate = useCallback(() => {
    setDateActivity(getPreviousDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const handleNextDate = useCallback(() => {
    setDateActivity(getNextDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const handleInvalidateRequestQueryTags = useCallback(() => {
    const tagId = `${data.employee.id}-${startDate}-${endDate}`;

    dispatch(
      reportsActivitiesService.util.invalidateTags([
        { type: reportsActivityQueryTag, id: tagId },
      ])
    );
  }, [data.employee.id, dispatch, endDate, startDate]);

  const onExpand = useCallback(
    (_: React.SyntheticEvent, expanded: boolean) => {
      setIsExpanded(expanded);

      if (expanded) {
        setIsOpenViewMode(false);
        handleInvalidateRequestQueryTags();
      }
    },
    [handleInvalidateRequestQueryTags]
  );

  const handleTeamLeadIcon = useCallback(() => {
    if (handleAddTeamLeadTab) {
      handleAddTeamLeadTab({ label: employee.name, id: employee.id });
    }
  }, [handleAddTeamLeadTab, employee.name, employee.id]);

  return (
    <>
      <Accordion
        expanded={isExpanded}
        onChange={onExpand}
        sx={styles.accordion}
        disableGutters
      >
        <AccordionSummary
          expandIcon={
            <IconButton>
              <ExpandMoreRoundedIcon />
            </IconButton>
          }
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={styles.accordionSummary}
        >
          <Box sx={styles.accordionSummaryInnerContainer}>
            <Box sx={styles.container}>
              <Typography sx={styles.name}>
                {generateUserLabel(employee.name, employee.internalId, employee.employeeType)}
              </Typography>
              {isTeamLead && showTeamLeadIcon && (
                <Tooltip title={'Team Lead'}>
                  <IconButton onClick={handleTeamLeadIcon}>
                    <PeopleAltIcon sx={styles.peopleMarker} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <ReportsEmployeeWorktime
              loggedHours={loggedHours}
              workingHours={workingHours}
              projectFilter={projectIdFilter}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={styles.toolboxWrapper}>
            <Box sx={styles.toolbox}>
              <IconButton onClick={handleOpenViewMode} sx={{ mr: 1 }}>
                <ViewModuleOutlinedIcon sx={styles.iconBtn} />
              </IconButton>
              {isOpenViewMode && (
                <AppViewModeSelector
                  viewMode={viewMode}
                  handleChangeViewMode={handleChangeViewMode}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <AppRow sx={styles.currentPeriodRow}>
              {isCustomDate && viewMode === ViewMode.OLD_VIEW ? (
                <Box sx={styles.customPeriodBox}>
                  <Typography variant="h1">
                    {startDateFormatted}&nbsp;-
                  </Typography>
                  <Typography variant="h1">&nbsp;{endDateFormatted}</Typography>
                </Box>
              ) : (
                <Box sx={styles.currentPeriodBox}>
                  <Button sx={styles.buttonArrow} onClick={handlePreviousDate}>
                    <ArrowBackIosRoundedIcon
                      fontSize={isMobile ? 'small' : 'medium'}
                    />
                  </Button>
                  <Button sx={styles.buttonArrow} onClick={handleNextDate}>
                    <ArrowForwardIosRoundedIcon
                      fontSize={isMobile ? 'small' : 'medium'}
                    />
                  </Button>
                  <Button sx={styles.buttonDate} onClick={handleDisplayDate}>
                    <Typography variant="h1">{displayDate}</Typography>
                  </Button>
                </Box>
              )}
              {isAddActivityButtonVisible && (
                <Button onClick={handleOpenAddActivity}>
                  <Typography
                    variant="h2"
                    fontSize={16}
                    sx={styles.gradientBtnTypography}
                  >
                    <span style={styles.plusSign}>+</span>
                    <u style={styles.gradientBtn}>Activity</u>
                  </Typography>
                </Button>
              )}
            </AppRow>

            {viewMode === ViewMode.OLD_VIEW ? (
              <AppActivityTableViews
                startDate={startDate}
                endDate={endDate}
                location={employee.location}
                activities={activities}
                employeeWorkHours={employee.workingHours}
                onEditActivity={onEditActivity}
                onDeleteActivity={onDeleteActivity}
                isEmployeeSection
                onAddActivity={onAddActivity}
              />
            ) : (
              <AppActivityViews
                activities={activities}
                dateActivity={dateActivity}
                location={employee.location}
                viewMode={viewMode}
                isLoading={isLoadingAll}
                onDayActivities={onDayActivities}
                onDeleteActivity={onDeleteActivity}
                onEditActivity={onEditActivity}
                onWeekActivities={onWeekActivities}
                showOnlyloggedTime={!!projectIdFilter}
                employeeWorkHours={employee.workingHours}
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <AppConfirmationSuccessful
        isOpen={successfulDialog.isOpen}
        message={successfulDialog.message}
        onClose={onCloseConfirmDialog}
      />

      <Dialog open={isOpenAddActivity} sx={{ borderRadius: '5px' }}>
        <AppDialogTitle
          title="Add activity"
          handleClose={handleCloseAddActivity}
        />
        <ActivityForm
          onClose={handleCloseAddActivity}
          idEmployee={employee.id}
          activity={selectedActivity}
          workingHours={data.employee.workingHours}
          onSubmit={onSubmitAddActivity}
          isLoading={isLoadingAll}
        />
      </Dialog>

      <Dialog open={isOpenEditActivity} sx={{ borderRadius: '5px' }}>
        <AppDialogTitle
          title="Edit activity"
          handleClose={handleOpenEditActivity}
        />
        <ActivityForm
          onClose={handleOpenEditActivity}
          idEmployee={employee.id}
          workingHours={data.employee.workingHours}
          onSubmit={onSubmitEditActivity}
          activity={selectedActivity}
          isLoading={isLoadingAll}
        />
      </Dialog>

      <AppConfirmationDelete
        isOpen={isOpenConfirmation}
        message={messageConfirmationDelete}
        onClose={onCloseDeleteDialog}
        onConfirm={onConfirmDeleteActivity}
      />
    </>
  );
}
